import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  signInWithPassword: async () => { },
  signUp: async () => { },
  signOut: async () => { },
  resetPasswordForEmail: async () => { },
});

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Guard in case supabase client wasn't configured (missing env vars on deploy)
    if (!supabase || typeof supabase.auth === 'undefined') {
      console.warn('Supabase client not initialized. Auth will be disabled.');
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    // try to get existing session
    (async () => {
      try {
        const result = await supabase.auth.getSession();
        if (!isMounted) return;
        const data = result?.data ?? {};
        setSession(data.session ?? null);
        setUser(data.session?.user ?? null);
      } catch (err) {
        console.error('Error getting supabase session:', err);
        setSession(null);
        setUser(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      try {
        setSession(newSession);
        setUser(newSession?.user ?? null);
      } catch (err) {
        console.error('Error handling auth state change:', err);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      try {
        // unsubscribe if possible
        subscription?.subscription?.unsubscribe?.();
      } catch (err) {
        // ignore
      }
    };
  }, []);

  const signInWithPassword = async ({ email, password }) => {
    if (!supabase || typeof supabase.auth === 'undefined') {
      throw new Error('Auth not available');
    }
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    
    // Explicitly block login if email is not verified
    if (data?.user && !data.user.email_confirmed_at) {
      await supabase.auth.signOut(); // Immediately destroy the session
      throw new Error('Please verify your email address before logging in. Check your inbox for the verification link.');
    }
    
    return data;
  };

  const signUp = async ({ email, password, options = {} }) => {
    if (!supabase || typeof supabase.auth === 'undefined') {
      throw new Error('Auth not available');
    }
    const { data, error } = await supabase.auth.signUp({ email, password, options });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    if (!supabase || typeof supabase.auth === 'undefined') {
      throw new Error('Auth not available');
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const verifyOtp = async ({ email, token, type = 'signup' }) => {
    if (!supabase || typeof supabase.auth === 'undefined') {
      throw new Error('Auth not available');
    }
    const { data, error } = await supabase.auth.verifyOtp({ email, token, type });
    if (error) throw error;
    return data;
  };

  const resetPasswordForEmail = async (email) => {
    if (!supabase || typeof supabase.auth === 'undefined') {
      throw new Error('Auth not available');
    }
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
    });
    if (error) throw error;
    return data;
  };

  const updatePassword = async (newPassword) => {
    if (!supabase || typeof supabase.auth === 'undefined') {
      throw new Error('Auth not available');
    }
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return data;
  };

  const value = useMemo(
    () => ({ user, session, loading, signInWithPassword, signUp, signOut, verifyOtp, resetPasswordForEmail, updatePassword }),
    [user, session, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);


